/**
 * Helpers for external video links (linked memories — open on source platform).
 */

export const VIDEO_LINK_HINT =
  'Paste a link from YouTube, Facebook, Instagram, TikTok, Vimeo, Loom, or another video page.';

export const VIDEO_EXTERNAL_NOTE = 'Opens on the original site.';

export const PROVIDER_CONFIG = {
  youtube: {
    label: 'YouTube',
    linkedFrom: 'Linked from YouTube',
    badgeClass: 'border-red-200/80 bg-red-50/95 text-red-900',
    markClass: 'bg-red-600 text-white',
    mark: 'YT',
  },
  vimeo: {
    label: 'Vimeo',
    linkedFrom: 'Linked from Vimeo',
    badgeClass: 'border-sky-200/80 bg-sky-50/95 text-sky-900',
    markClass: 'bg-sky-600 text-white',
    mark: 'Vi',
  },
  loom: {
    label: 'Loom',
    linkedFrom: 'Linked from Loom',
    badgeClass: 'border-violet-200/80 bg-violet-50/95 text-violet-900',
    markClass: 'bg-violet-600 text-white',
    mark: 'Lo',
  },
  facebook: {
    label: 'Facebook',
    linkedFrom: 'Linked from Facebook',
    badgeClass: 'border-blue-200/80 bg-blue-50/95 text-blue-900',
    markClass: 'bg-[#1877F2] text-white',
    mark: 'f',
  },
  instagram: {
    label: 'Instagram',
    linkedFrom: 'Linked from Instagram',
    badgeClass: 'border-fuchsia-200/80 bg-gradient-to-br from-fuchsia-50 to-orange-50 text-fuchsia-950',
    markClass: 'bg-gradient-to-br from-fuchsia-600 to-orange-500 text-white',
    mark: 'IG',
  },
  tiktok: {
    label: 'TikTok',
    linkedFrom: 'Linked from TikTok',
    badgeClass: 'border-neutral-300/80 bg-neutral-50 text-neutral-900',
    markClass: 'bg-neutral-900 text-white',
    mark: 'TT',
  },
  external: {
    label: 'Video link',
    linkedFrom: 'Linked video',
    badgeClass: 'border-[hsl(38_14%_82%)] bg-[hsl(42_28%_96%)] text-[hsl(32_10%_38%)]',
    markClass: 'bg-[#8b7355] text-white',
    mark: '▶',
  },
};

export function getVideoLink(entity) {
  if (!entity) return null;
  if (entity.video_link) return entity.video_link;
  if (!entity.video_url) return null;
  return {
    video_url: entity.video_url,
    video_provider: entity.video_provider,
    video_title: entity.video_title,
    video_notes: entity.video_notes,
    video_thumbnail_url: entity.video_thumbnail_url,
    video_watch_url: entity.video_url,
    embeddable: ['youtube', 'vimeo', 'loom'].includes(entity.video_provider),
  };
}

export function hasVideoLink(entity) {
  return Boolean(getVideoLink(entity)?.video_url);
}

export function getProviderConfig(provider) {
  return PROVIDER_CONFIG[provider] || PROVIDER_CONFIG.external;
}

export function getProviderLabel(provider) {
  return getProviderConfig(provider).label;
}

export function getLinkedFromLabel(provider, link) {
  if (link?.video_provider_label) {
    return `Linked from ${link.video_provider_label}`;
  }
  return getProviderConfig(provider).linkedFrom;
}

export function getProviderBadgeClass(provider) {
  return getProviderConfig(provider).badgeClass;
}

export function getOpenVideoLabel() {
  return 'Open linked video';
}

/** Display title — never repeated elsewhere in the card layout. */
export function getVideoDisplayTitle(link) {
  const title = (link?.video_title || '').trim();
  return title || 'Linked video';
}
